/**
 * Kiehl's Bundle Product Tabs
 * Fetches collection products via Storefront GraphQL on tab click.
 * All tab merges products from every tab collection (deduped by product id).
 * Requires window.baseURL + window.storefrontAccessTokenGQL (kiehls-storefront-api snippet).
 */
(function () {
  const GQL_QUERY = `
    query GetCollectionProducts($collectionId: ID!, $first: Int!, $after: String) {
      collection(id: $collectionId) {
        products(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              handle
              description
              onlineStoreUrl
              availableForSale
              featuredImage { url altText }
              priceRange {
                minVariantPrice { amount currencyCode }
              }
              variants(first: 50) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price { amount currencyCode }
                    compareAtPrice { amount currencyCode }
                    selectedOptions { name value }
                  }
                }
              }
              metafields(identifiers: [
                { namespace: "reviews", key: "rating" },
                { namespace: "reviews", key: "rating_count" },
                { namespace: "judgeme", key: "badge" }
              ]) {
                namespace
                key
                value
              }
            }
          }
        }
      }
    }
  `;

  const ALL_PAGE_SIZE = 20;
  const COLLECTION_PAGE_SIZE = 50;
  const MAX_COLLECTION_PRODUCTS = 500;

  function getConfig(root) {
    const el = root.querySelector('[data-kbt-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[KiehlsBundleTabs] Invalid config JSON', e);
      return null;
    }
  }

  function getId(gid) {
    if (!gid) return '';
    if (String(gid).includes('/')) return String(gid).split('/').pop();
    try {
      return window.atob(gid).split('/').pop();
    } catch (e) {
      return gid;
    }
  }

  function formatMoney(amount, currencyCode) {
    const num = parseFloat(amount);
    if (Number.isNaN(num)) return '';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currencyCode || 'INR',
        maximumFractionDigits: 0,
      }).format(num);
    } catch (e) {
      return '₹ ' + Math.round(num).toLocaleString('en-IN');
    }
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function stripHtml(html) {
    let clean = String(html || '')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<!--[\s\S]*?-->/g, ' ');

    const tmp = document.createElement('div');
    tmp.innerHTML = clean;
    tmp.querySelectorAll('style, script, noscript').forEach((el) => el.remove());

    let text = (tmp.textContent || tmp.innerText || '')
      .replace(/\.[\w-]+\s*\{[^}]*\}/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // If leftover is mostly CSS (A+ / style blocks), hide excerpt
    if (!text) return '';
    if (/aplus-container/i.test(text)) return '';
    if (/\{[^}]*\}/.test(text) && /\.[\w-]+/.test(text)) return '';

    return text;
  }

  function truncate(str, len) {
    if (!str) return '';
    return str.length > len ? str.slice(0, len).trim() + '…' : str;
  }

  function getMeta(product, key) {
    if (!product.metafields) return '';
    const list = Array.isArray(product.metafields)
      ? product.metafields
      : product.metafields.edges?.map((e) => e.node) || [];
    const meta = list.find((m) => m && m.key === key);
    return meta?.value || '';
  }

  function parseRating(product) {
    const ratingVal = getMeta(product, 'rating');
    const ratingCount = getMeta(product, 'rating_count');
    let value = 0;
    let count = ratingCount || 0;
    if (ratingVal) {
      try {
        const obj = JSON.parse(ratingVal);
        value = obj?.value ? parseFloat(obj.value) : parseFloat(ratingVal) || 0;
        if (obj?.count && !count) count = obj.count;
      } catch (e) {
        value = parseFloat(ratingVal) || 0;
      }
    }
    return { value, count };
  }

  function starRow(value) {
    const full = Math.floor(value);
    let html = '';
    for (let i = 0; i < 5; i++) {
      const filled = i < full || (i === full && value - full >= 0.5);
      html += `<span class="kbt-card__star${filled ? ' is-filled' : ''}" aria-hidden="true">★</span>`;
    }
    return html;
  }

  function getVariantLabel(v) {
    const opts = v.selectedOptions || [];
    if (opts.length === 1) return opts[0].value;
    const sizeOpt = opts.find((o) => /size|pack/i.test(o.name || ''));
    if (sizeOpt) return sizeOpt.value;
    if (v.title && v.title !== 'Default Title') return v.title;
    return opts.map((o) => o.value).join(' / ') || 'Default';
  }

  function buildVariantOptions(variants, selectedId) {
    return variants
      .map((v) => {
        const label = getVariantLabel(v);
        const displayLabel = v.availableForSale ? label : `${label} - Unavailable`;
        const id = getId(v.id);
        // Keep unavailable options selectable (same as collections/all).
        return `<option value="${escapeHtml(id)}"${
          id === selectedId ? ' selected' : ''
        } data-price="${escapeHtml(
          v.price?.amount || ''
        )}" data-compare="${escapeHtml(
          v.compareAtPrice?.amount || ''
        )}" data-currency="${escapeHtml(v.price?.currencyCode || 'INR')}" data-available="${
          v.availableForSale ? 'true' : 'false'
        }">${escapeHtml(displayLabel)}</option>`;
      })
      .join('');
  }

  function formatCardPriceHTML(price, compare, currency) {
    const priceNum = parseFloat(price) || 0;
    const compareNum = parseFloat(compare) || 0;
    const priceHtml = `<span class="kbt-card__price-current">${formatMoney(
      priceNum,
      currency
    )}</span>`;

    if (compareNum <= priceNum) return priceHtml;

    const discount = Math.round(((compareNum - priceNum) / compareNum) * 100);
    const compareHtml = `<span class="kbt-card__price-compare">${formatMoney(
      compareNum,
      currency
    )}</span>`;
    const discountHtml =
      discount > 0 ? `<span class="kbt-card__price-discount">${discount}% OFF</span>` : '';

    return `${compareHtml}${priceHtml}${discountHtml}`;
  }

  function registerDoranProduct(product) {
    window.$bpDoranInit = window.$bpDoranInit || {};
    window.$bpDoranInit.backInStockProducts = window.$bpDoranInit.backInStockProducts || {};
    const productId = getId(product.id);
    if (!productId) return;
    const variants = (product.variants?.edges || []).map((e) => e.node).filter(Boolean);
    window.$bpDoranInit.backInStockProducts[productId] = {
      id: productId,
      title: (product.title || '').replace(/"/g, "'"),
      handle: product.handle || '',
      tags: [],
      collections: [],
      image: product.featuredImage?.url || '',
      variants: variants.map((v) => ({
        id: getId(v.id),
        title: (v.title || '').replace(/"/g, "'"),
        sku: '',
        management: 'shopify',
        policy: 'deny',
        quantity: v.availableForSale ? 1 : 0,
      })),
    };
  }

  function applyCardCtaState(addBtn, available, notifyLabel) {
    if (!addBtn || addBtn.classList.contains('is-added')) return;
    const ctaLabel = addBtn.getAttribute('data-original-label') || 'ADD PRODUCT';
    if (available) {
      addBtn.textContent = ctaLabel;
      addBtn.classList.remove('is-notify');
      addBtn.removeAttribute('data-kbt-notify');
      addBtn.disabled = false;
      return;
    }
    addBtn.textContent = notifyLabel || 'Notify Me';
    addBtn.classList.add('is-notify');
    addBtn.setAttribute('data-kbt-notify', 'true');
    addBtn.disabled = false;
  }

  function buildCardHTML(product, config) {
    registerDoranProduct(product);
    const variants = (product.variants?.edges || []).map((e) => e.node).filter(Boolean);
    const firstAvailable = variants.find((v) => v.availableForSale) || variants[0] || {};
    const url = product.onlineStoreUrl || `/products/${product.handle}`;
    const imgSrc = product.featuredImage?.url || '';
    const imgAlt = product.featuredImage?.altText || product.title || '';
    const title = product.title || '';
    const excerpt = truncate(stripHtml(product.description), 100);
    const rating = parseRating(product);
    const productId = getId(product.id);
    const priceAmount =
      firstAvailable.price?.amount || product.priceRange?.minVariantPrice?.amount || '0';
    const compareAmount = firstAvailable.compareAtPrice?.amount || '';
    const currency =
      firstAvailable.price?.currencyCode ||
      product.priceRange?.minVariantPrice?.currencyCode ||
      'INR';
    const hasMultiVariants = variants.length > 1;
    const packLabel = config.packSizeLabel || 'Pack Size';
    const oneSizeLabel = config.oneSizeLabel || 'One Size Only';
    const ctaLabel = config.addProductLabel || 'ADD PRODUCT';
    const notifyLabel = config.notifyMeLabel || 'Notify Me';
    const selectedId = getId(firstAvailable.id);
    const isAvailable = Boolean(firstAvailable.availableForSale);
    const singleLabel = getVariantLabel(firstAvailable);
    const singleDisplay = isAvailable ? singleLabel : `${singleLabel} - Unavailable`;

    const ratingsHTML =
      rating.value > 0
        ? `<div class="kbt-card__rating" aria-label="${rating.value} out of 5">
            <div class="kbt-card__stars">${starRow(rating.value)}</div>
            <span class="kbt-card__rating-text">${rating.value}${
              rating.count ? ` (${rating.count})` : ''
            }</span>
          </div>`
        : `<div class="kbt-card__rating kbt-card__rating--empty"></div>`;

    const variantHTML = hasMultiVariants
      ? `<div class="kbt-card__variant">
          <label class="kbt-card__variant-label" for="kbt-variant-${config.sectionId}-${productId}">${escapeHtml(packLabel)}</label>
          <select class="kbt-card__variant-select" id="kbt-variant-${config.sectionId}-${productId}" data-kbt-variant>
            ${buildVariantOptions(variants, selectedId)}
          </select>
        </div>`
      : `<div class="kbt-card__variant kbt-card__variant--single">
          <p class="kbt-card__variant-one-label">${escapeHtml(oneSizeLabel)}</p>
          <p class="kbt-card__variant-one-value">${escapeHtml(singleDisplay)}</p>
          <input type="hidden" data-kbt-variant value="${escapeHtml(getId(firstAvailable.id))}" data-price="${escapeHtml(
            priceAmount
          )}" data-compare="${escapeHtml(
            compareAmount
          )}" data-currency="${escapeHtml(currency)}" data-available="${
            isAvailable ? 'true' : 'false'
          }">
        </div>`;

    const imgHTML = imgSrc
      ? `<img class="kbt-card__image" src="${escapeHtml(imgSrc)}" alt="${escapeHtml(
          imgAlt
        )}" loading="lazy" width="400" height="400">`
      : `<div class="kbt-card__image kbt-card__image--placeholder" aria-hidden="true"></div>`;

    const ctaClass = isAvailable ? 'kbt-card__cta' : 'kbt-card__cta is-notify';
    const ctaText = isAvailable ? ctaLabel : notifyLabel;
    const ctaNotifyAttr = isAvailable ? '' : ' data-kbt-notify="true"';

    return `
      <article class="kbt-card" data-product-id="${escapeHtml(productId)}" data-product-url="${escapeHtml(url)}">
        <a class="kbt-card__media" href="${escapeHtml(url)}">${imgHTML}</a>
        <div class="kbt-card__body">
          <h3 class="kbt-card__title">
            <a href="${escapeHtml(url)}">${escapeHtml(title)}</a>
          </h3>
          <div class="kbt-card__excerpt-wrap">
            ${excerpt ? `<p class="kbt-card__excerpt">${escapeHtml(excerpt)}</p>` : ''}
          </div>
          ${ratingsHTML}
          ${variantHTML}
          <div class="kbt-card__price" data-kbt-price>${formatCardPriceHTML(
            priceAmount,
            compareAmount,
            currency
          )}</div>
          <div
            class="doran-collection-bis-button"
            data-dr="back-in-stock-inline-button"
            data-product-id="${escapeHtml(productId)}"
            data-selected-variant-id="${escapeHtml(selectedId)}"
          ></div>
          <button
            type="button"
            class="${ctaClass}"
            data-kbt-add
            data-original-label="${escapeHtml(ctaLabel)}"
            ${ctaNotifyAttr}
          >${escapeHtml(ctaText)}</button>
        </div>
      </article>`;
  }

  async function fetchProductsPage(collectionId, first, after = null) {
    const baseURL = window.baseURL;
    const token = window.storefrontAccessTokenGQL;
    if (!baseURL || !token) {
      console.warn(
        '[KiehlsBundleTabs] Storefront API config missing. Set Theme settings → Storefront API token.'
      );
      return null;
    }
    try {
      const variables = { collectionId, first };
      if (after) variables.after = after;

      const res = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: GQL_QUERY,
          variables,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.errors?.length) {
        console.error('[KiehlsBundleTabs] GraphQL errors:', json.errors);
      }
      const connection = json?.data?.collection?.products;
      return {
        products: connection?.edges?.map((e) => e.node) || [],
        pageInfo: connection?.pageInfo || { hasNextPage: false, endCursor: null },
      };
    } catch (err) {
      console.error('[KiehlsBundleTabs] GraphQL fetch error:', err);
      return null;
    }
  }

  async function fetchAllCollectionProducts(collectionId) {
    const products = [];
    let after = null;
    let hasNextPage = true;

    while (hasNextPage && products.length < MAX_COLLECTION_PRODUCTS) {
      const remaining = MAX_COLLECTION_PRODUCTS - products.length;
      const page = await fetchProductsPage(
        collectionId,
        Math.min(COLLECTION_PAGE_SIZE, remaining),
        after
      );
      if (!page) return products.length ? products : null;
      products.push(...page.products);
      hasNextPage = Boolean(page.pageInfo?.hasNextPage);
      after = page.pageInfo?.endCursor || null;
      if (!page.products.length) break;
    }

    return products;
  }

  function skeletonHTML(count) {
    let html = '';
    for (let i = 0; i < count; i++) {
      html += `
        <div class="kbt-skeleton" aria-hidden="true">
          <div class="kbt-skeleton__image"></div>
          <div class="kbt-skeleton__line" style="width:90%"></div>
          <div class="kbt-skeleton__line" style="width:70%"></div>
          <div class="kbt-skeleton__line" style="width:40%"></div>
        </div>`;
    }
    return html;
  }

  /* ── Sticky Bar State (persisted across page refresh) ── */
  const STICKY_STORAGE_KEY = 'kiehls-bundle-sticky-items';
  const stickyItems = new Map();

  function saveStickyItems() {
    try {
      const payload = Array.from(stickyItems.entries());
      localStorage.setItem(STICKY_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.warn('[KiehlsBundleTabs] Unable to save sticky items', err);
    }
  }

  function loadStickyItems() {
    try {
      const raw = localStorage.getItem(STICKY_STORAGE_KEY);
      if (!raw) return;
      const payload = JSON.parse(raw);
      if (!Array.isArray(payload)) return;
      stickyItems.clear();
      payload.forEach((entry) => {
        if (!Array.isArray(entry) || entry.length < 2) return;
        const [key, item] = entry;
        if (!key || !item || !item.variantId) return;
        stickyItems.set(String(key), {
          variantId: String(item.variantId),
          price: parseFloat(item.price) || 0,
          compare: parseFloat(item.compare) || 0,
          currency: item.currency || 'INR',
          title: item.title || '',
          imgSrc: item.imgSrc || '',
        });
      });
    } catch (err) {
      console.warn('[KiehlsBundleTabs] Unable to load sticky items', err);
    }
  }

  function clearStickyItems() {
    stickyItems.clear();
    try {
      localStorage.removeItem(STICKY_STORAGE_KEY);
    } catch (err) {
      console.warn('[KiehlsBundleTabs] Unable to clear sticky items', err);
    }
  }

  function getSelectedStickyKey(card) {
    const productId = card.getAttribute('data-product-id') || '';
    const info = getVariantInfo(card);
    if (!productId || !info.variantId) return '';
    return productId + '-' + info.variantId;
  }

  function syncCardAddedStates(root) {
    if (!root) return;
    const config = getConfig(root);
    const notifyLabel = config?.notifyMeLabel || 'Notify Me';

    root.querySelectorAll('.kbt-card').forEach((card) => {
      const addBtn = card.querySelector('[data-kbt-add]');
      if (!addBtn) return;

      const key = getSelectedStickyKey(card);
      const isAdded = Boolean(key && stickyItems.has(key));

      if (isAdded) {
        addBtn.setAttribute('data-sticky-key', key);
        addBtn.textContent = 'ADDED!';
        addBtn.classList.add('is-added');
        addBtn.classList.remove('is-notify');
        addBtn.removeAttribute('data-kbt-notify');
        addBtn.disabled = false;
        return;
      }

      addBtn.classList.remove('is-added');
      addBtn.removeAttribute('data-sticky-key');

      const select = card.querySelector('select[data-kbt-variant]');
      const hidden = card.querySelector('input[data-kbt-variant]');
      const opt = select ? select.options[select.selectedIndex] : hidden;
      const available = opt?.getAttribute('data-available') === 'true';
      applyCardCtaState(addBtn, available, notifyLabel);
    });
  }

  function getStickyEls(root) {
    const bar = root.querySelector('[data-kbt-sticky]');
    return {
      bar,
      itemsShell: root.querySelector('[data-kbt-sticky-items-shell]'),
      swiperEl: root.querySelector('[data-kbt-sticky-swiper]'),
      itemsContainer: root.querySelector('[data-kbt-sticky-items]'),
      prevEl: root.querySelector('[data-kbt-sticky-prev]'),
      nextEl: root.querySelector('[data-kbt-sticky-next]'),
      priceEl: root.querySelector('[data-kbt-sticky-price]'),
      compareEl: root.querySelector('[data-kbt-sticky-compare]'),
      discountEl: root.querySelector('[data-kbt-sticky-discount]'),
      countEl: root.querySelector('[data-kbt-sticky-count]'),
      countLabelEl: root.querySelector('[data-kbt-sticky-count-label]'),
      ctaBtn: root.querySelector('[data-kbt-sticky-add]'),
      limitEl: root.querySelector('[data-kbt-sticky-limit-message]'),
      totalsEl: bar?.querySelector('.kbt-sticky__totals') || null,
    };
  }

  function getConfiguredStickySlots(root) {
    const bar = root?.querySelector('[data-kbt-sticky]');
    return Math.max(
      1,
      parseInt(bar?.getAttribute('data-sticky-slots') || '6', 10) || 6
    );
  }

  function showStickyLimitMessage(root, limit) {
    const messageEl = root?.querySelector('[data-kbt-sticky-limit-message]');
    if (!messageEl) return;

    const noun = limit === 1 ? 'item' : 'items';
    messageEl.textContent = `You can only add ${limit} ${noun} in your cart`;
    messageEl.classList.add('is-visible');

    window.clearTimeout(messageEl._kbtLimitTimer);
    messageEl._kbtLimitTimer = window.setTimeout(() => {
      messageEl.classList.remove('is-visible');
    }, 3000);
  }

  function destroyStickySwiper(els) {
    const instance = els.swiperEl?._kbtStickySwiper;
    if (instance) instance.destroy(true, true);
    if (els.swiperEl) {
      els.swiperEl._kbtStickySwiper = null;
      els.swiperEl._kbtSwiperPending = false;
      els.swiperEl.style.width = '';
    }
    els.itemsShell?.classList.remove('is-slider');
  }

  function whenSwiperReady(callback) {
    if (typeof window.Swiper === 'function') {
      callback();
      return;
    }

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (typeof window.Swiper === 'function') {
        window.clearInterval(timer);
        callback();
      } else if (tries > 60) {
        window.clearInterval(timer);
      }
    }, 50);
  }

  function initStickySwiper(els, slideCount, visibleSlides, isMobile) {
    // Slots > visible preview (6 desktop / 4 mobile) → slider + arrows,
    // even when all slots are still empty placeholders.
    if (slideCount <= visibleSlides || !els.swiperEl) return;

    const startSwiper = () => {
      if (!els.swiperEl || els.swiperEl._kbtStickySwiper) return;
      if (typeof window.Swiper !== 'function') return;

      const itemSize = isMobile ? 40 : 60;
      const spaceBetween = isMobile ? 16 : 20;
      const slidesPerView = visibleSlides + 0.5;
      const sliderWidth =
        slidesPerView * itemSize + (slidesPerView - 1) * spaceBetween;

      els.itemsShell?.classList.add('is-slider');
      els.swiperEl.style.width = `${sliderWidth}px`;
      els.swiperEl._kbtStickySwiper = new window.Swiper(els.swiperEl, {
        slidesPerView,
        slidesPerGroup: 1,
        spaceBetween,
        speed: 450,
        watchOverflow: true,
        grabCursor: true,
        navigation:
          els.prevEl && els.nextEl
            ? {
                prevEl: els.prevEl,
                nextEl: els.nextEl,
              }
            : false,
      });
    };

    if (typeof window.Swiper === 'function') {
      startSwiper();
      return;
    }

    // Swiper asset is deferred — retry once it is available.
    if (els.swiperEl._kbtSwiperPending) return;
    els.swiperEl._kbtSwiperPending = true;
    whenSwiperReady(() => {
      if (!els.swiperEl || !els.swiperEl.isConnected) return;
      els.swiperEl._kbtSwiperPending = false;
      const currentSlides = els.swiperEl.querySelectorAll('.swiper-slide').length;
      if (currentSlides <= visibleSlides) return;
      startSwiper();
    });
  }

  function refreshStickyItemPricing(root, key, item) {
    const dashIdx = String(key).indexOf('-');
    if (dashIdx === -1) return item;

    const productId = String(key).slice(0, dashIdx);
    const variantId = String(key).slice(dashIdx + 1);
    const card = root.querySelector(`.kbt-card[data-product-id="${productId}"]`);
    if (!card) return item;

    const select = card.querySelector('select[data-kbt-variant]');
    const hidden = card.querySelector('input[data-kbt-variant]');
    let el = null;

    if (select) {
      el = Array.from(select.options).find((o) => o.value === variantId);
    } else if (hidden && hidden.value === variantId) {
      el = hidden;
    }
    if (!el) return item;

    const price = parseFloat(el.getAttribute('data-price') || '') || item.price;
    const compareRaw = el.getAttribute('data-compare');
    const compare = compareRaw ? parseFloat(compareRaw) : 0;

    return { ...item, price, compare };
  }

  function addStickyLineTotals(item, totals) {
    const price = parseFloat(item.price) || 0;
    const compare = parseFloat(item.compare) || 0;
    totals.price += price;
    totals.compare += compare > price ? compare : price;
  }

  function syncStickyPageOffset(bar) {
    const height = bar ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    document.body.style.paddingBottom = height ? `${height}px` : '';
  }

  function updateStickyBar(root) {
    const els = getStickyEls(root);
    if (!els.bar) return;

    const isMobileSticky = window.matchMedia('(max-width: 749px)').matches;
    const configuredSlots = getConfiguredStickySlots(root);
    const visibleSlides = Math.min(configuredSlots, isMobileSticky ? 4 : 6);

    // Enforce a reduced customizer limit against previously saved selections.
    if (stickyItems.size > configuredSlots) {
      Array.from(stickyItems.keys())
        .slice(configuredSlots)
        .forEach((key) => stickyItems.delete(key));
      syncCardAddedStates(root);
    }

    const count = stickyItems.size;
    els.bar.hidden = false;
    els.bar.classList.add('is-visible');

    stickyItems.forEach((item, key) => {
      stickyItems.set(key, refreshStickyItemPricing(root, key, item));
    });

    const entries = Array.from(stickyItems.entries());
    const totals = { price: 0, compare: 0 };
    const slideCount = configuredSlots;

    destroyStickySwiper(els);
    els.itemsContainer.innerHTML = '';

    // Always render the configured number of slots; selected products fill them first.
    for (let i = 0; i < slideCount; i++) {
      const entry = entries[i];
      if (!entry) {
        const placeholder = document.createElement('div');
        placeholder.className =
          'kbt-sticky__item kbt-sticky__item--placeholder swiper-slide';
        els.itemsContainer.appendChild(placeholder);
        continue;
      }

      const [key, item] = entry;
      addStickyLineTotals(item, totals);

      const slide = document.createElement('div');
      slide.className = 'kbt-sticky__item swiper-slide';
      slide.innerHTML = `
        <div class="kbt-sticky__item-media">
          ${item.imgSrc ? `<img src="${escapeHtml(item.imgSrc)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
        </div>
        <button type="button" class="kbt-sticky__item-remove" data-kbt-sticky-remove="${escapeHtml(key)}" aria-label="Remove ${escapeHtml(item.title)}">
          <span class="kbt-sticky__item-remove-icon" aria-hidden="true"></span>
        </button>
      `;
      els.itemsContainer.appendChild(slide);
    }

    els.itemsContainer.querySelectorAll('[data-kbt-sticky-remove]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const removeKey = btn.getAttribute('data-kbt-sticky-remove');
        stickyItems.delete(removeKey);
        syncCardAddedStates(root);
        updateStickyBar(root);
      });
    });

    initStickySwiper(els, slideCount, visibleSlides, isMobileSticky);

    const currency = stickyItems.values().next().value?.currency || 'INR';
    const totalPrice = totals.price;
    const totalCompare = totals.compare;

    if (els.priceEl) els.priceEl.textContent = formatMoney(totalPrice, currency);

    if (els.compareEl) {
      if (count > 0 && totalCompare > totalPrice) {
        els.compareEl.textContent = formatMoney(totalCompare, currency);
      } else {
        els.compareEl.textContent = '';
      }
      els.compareEl.removeAttribute('style');
      els.compareEl.hidden = false;
    }

    if (els.discountEl) {
      if (count > 0 && totalCompare > totalPrice) {
        const pct = Math.round(((totalCompare - totalPrice) / totalCompare) * 100);
        els.discountEl.textContent = pct + '% Off';
      } else {
        els.discountEl.textContent = '';
      }
    }

    // Empty box: Customizer Product slots. Once items are added: live box count.
    const displayCount = count > 0 ? count : configuredSlots;
    if (els.countEl) els.countEl.textContent = displayCount;
    if (els.countLabelEl) els.countLabelEl.textContent = displayCount === 1 ? 'Item' : 'Items';
    if (els.ctaBtn) els.ctaBtn.disabled = count === 0;

    // Empty state UI should match the reference (hide totals/labels)
    if (els.totalsEl) els.totalsEl.style.display = count === 0 ? 'none' : '';

    saveStickyItems();
    requestAnimationFrame(() => syncStickyPageOffset(els.bar));
  }

  function getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  function dispatchCartAddEvent(response, itemCount) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: response,
          sourceId: 'kbt-sticky',
          data: {
            source: 'product-form-component',
            itemCount,
            sections: response.sections,
          },
        },
      })
    );
  }

  function animateStickyFlyToCart(root) {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const els = getStickyEls(root);
    const firstItem = stickyItems.values().next().value;
    if (!cartIcon || !firstItem?.imgSrc) return;

    const source =
      els.itemsContainer?.querySelector('.kbt-sticky__item-media img') || els.ctaBtn;
    if (!source) return;

    const fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--main');
    fly.style.setProperty('background-image', `url(${firstItem.imgSrc})`);
    fly.style.setProperty('--start-opacity', '0');
    fly.source = source;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  function initStickyCartButton(root) {
    const els = getStickyEls(root);
    if (!els.ctaBtn) return;

    els.ctaBtn.addEventListener('click', async () => {
      if (stickyItems.size === 0) return;
      const items = [];
      stickyItems.forEach((item) => {
        items.push({ id: Number(item.variantId), quantity: 1 });
      });

      els.ctaBtn.disabled = true;
      els.ctaBtn.classList.add('is-loading');
      const originalText = els.ctaBtn.innerHTML;

      try {
        const sectionIds = getCartSectionIds();
        const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
        const res = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || `Cart add failed: ${res.status}`);
        }

        dispatchCartAddEvent(data, items.length);
        animateStickyFlyToCart(root);

        const floCartBtn = window.handleFloCartBtn;
        if (typeof floCartBtn === 'function') {
          floCartBtn();
        }

        els.ctaBtn.innerHTML = 'ADDED TO CART!';
        setTimeout(() => {
          clearStickyItems();
          root.querySelectorAll('[data-kbt-add].is-added').forEach((btn) => {
            btn.textContent = btn.getAttribute('data-original-label') || 'ADD PRODUCT';
            btn.classList.remove('is-added');
            btn.disabled = false;
            btn.removeAttribute('data-sticky-key');
          });
          els.ctaBtn.innerHTML = originalText;
          els.ctaBtn.classList.remove('is-loading');
          updateStickyBar(root);
        }, 1500);
      } catch (err) {
        console.error('[KiehlsBundleTabs] Bulk add to cart error:', err);
        els.ctaBtn.innerHTML = originalText;
        els.ctaBtn.disabled = false;
        els.ctaBtn.classList.remove('is-loading');
      }
    });
  }

  function getVariantInfo(card) {
    const select = card.querySelector('select[data-kbt-variant]');
    const hidden = card.querySelector('input[data-kbt-variant]');
    let variantId = '';
    let price = 0;
    let compare = 0;
    let currency = 'INR';

    if (select) {
      const opt = select.options[select.selectedIndex];
      variantId = select.value;
      price = parseFloat(opt?.getAttribute('data-price') || '0');
      currency = opt?.getAttribute('data-currency') || 'INR';
    } else if (hidden) {
      variantId = hidden.value;
      price = parseFloat(hidden.getAttribute('data-price') || '0');
      currency = hidden.getAttribute('data-currency') || 'INR';
    }

    const compareOpt = select
      ? select.options[select.selectedIndex]
      : hidden;
    const compareStr = compareOpt?.getAttribute('data-compare') || '';
    compare = parseFloat(compareStr) || 0;

    return { variantId, price, compare, currency };
  }

  function bindCardEvents(grid) {
    const root = grid.closest('[data-kbt-section]');
    const config = getConfig(root);
    const notifyLabel = config?.notifyMeLabel || 'Notify Me';

    grid.querySelectorAll('.kbt-card').forEach((card) => {
      if (card.dataset.kbtBound === 'true') return;
      card.dataset.kbtBound = 'true';

      const select = card.querySelector('select[data-kbt-variant]');
      const hidden = card.querySelector('input[data-kbt-variant]');
      const priceEl = card.querySelector('[data-kbt-price]');
      const addBtn = card.querySelector('[data-kbt-add]');
      const doranEl = card.querySelector('.doran-collection-bis-button');
      const productId = card.getAttribute('data-product-id') || '';
      const imgEl = card.querySelector('.kbt-card__image');
      const titleEl = card.querySelector('.kbt-card__title');

      if (addBtn && !addBtn.hasAttribute('data-original-label')) {
        addBtn.setAttribute('data-original-label', addBtn.textContent.trim());
      }

      const syncFromOption = (opt) => {
        if (!opt) return;
        const amount = opt.getAttribute('data-price');
        const compare = opt.getAttribute('data-compare') || '';
        const currency = opt.getAttribute('data-currency') || 'INR';
        const available = opt.getAttribute('data-available') === 'true';
        const variantId = opt.value || opt.getAttribute('value') || '';

        if (priceEl && amount != null) {
          priceEl.innerHTML = formatCardPriceHTML(amount, compare, currency);
        }
        if (doranEl && variantId) {
          doranEl.setAttribute('data-selected-variant-id', variantId);
        }
        applyCardCtaState(addBtn, available, notifyLabel);
      };

      if (select) {
        select.addEventListener('change', () => {
          syncFromOption(select.options[select.selectedIndex]);
          // Keep other size variants in sticky; only refresh button for this selection.
          syncCardAddedStates(root);
        });
      }

      if (addBtn) {
        addBtn.addEventListener('click', () => {
          if (addBtn.classList.contains('is-added')) return;

          const info = getVariantInfo(card);
          if (!info.variantId) return;

          // Unavailable: open PDP (Doran Notify Me form lives there / may also paint inline).
          if (addBtn.getAttribute('data-kbt-notify') === 'true') {
            const doranBtn = doranEl?.querySelector('button');
            if (doranBtn) {
              doranBtn.click();
              return;
            }
            const baseUrl =
              card.getAttribute('data-product-url') ||
              card.querySelector('.kbt-card__media')?.getAttribute('href') ||
              '';
            if (!baseUrl) return;
            const sep = baseUrl.includes('?') ? '&' : '?';
            window.location.href = `${baseUrl}${sep}variant=${encodeURIComponent(info.variantId)}`;
            return;
          }

          const key = productId + '-' + info.variantId;

          const stickyLimit = getConfiguredStickySlots(root);
          if (stickyItems.size >= stickyLimit && !stickyItems.has(key)) {
            showStickyLimitMessage(root, stickyLimit);
            return;
          }

          addBtn.setAttribute('data-sticky-key', key);

          stickyItems.set(key, {
            variantId: info.variantId,
            price: info.price,
            compare: info.compare,
            currency: info.currency,
            title: titleEl?.textContent?.trim() || '',
            imgSrc: imgEl?.src || imgEl?.querySelector('img')?.src || '',
          });

          addBtn.textContent = 'ADDED!';
          addBtn.classList.add('is-added');
          addBtn.classList.remove('is-notify');
          addBtn.removeAttribute('data-kbt-notify');

          updateStickyBar(root);
        });
      }
    });

    syncCardAddedStates(root);
  }

  function updateTabArrows(root) {
    const track = root.querySelector('[data-kbt-tabs-track]');
    const prev = root.querySelector('[data-kbt-tabs-prev]');
    const next = root.querySelector('[data-kbt-tabs-next]');
    if (!track || !prev || !next) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }

    const maxScroll = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= maxScroll - 2;
    const needsScroll = maxScroll > 4;
    prev.hidden = !needsScroll;
    next.hidden = !needsScroll;
  }

  function initSection(root) {
    const config = getConfig(root);
    if (!config) return;

    const tabs = root.querySelectorAll('[data-kbt-tab]');
    const panels = root.querySelectorAll('[data-kbt-panel]');
    const track = root.querySelector('[data-kbt-tabs-track]');
    const prev = root.querySelector('[data-kbt-tabs-prev]');
    const next = root.querySelector('[data-kbt-tabs-next]');
    const cache = new Map();
    const skeletonCount = 8;
    const allPageSize = config.allPageSize || ALL_PAGE_SIZE;

    let allProductsList = [];
    let allRenderedCount = 0;
    let allLoadingMore = false;
    let allObserver = null;

    function getTabCollectionIds() {
      const ids = [];
      panels.forEach((panel) => {
        if (panel.hasAttribute('data-kbt-all')) return;
        const id = panel.getAttribute('data-collection-id');
        if (!id || id === 'gid://shopify/Collection/') return;
        if (!ids.includes(id)) ids.push(id);
      });
      return ids;
    }

    function disconnectAllObserver() {
      if (allObserver) {
        allObserver.disconnect();
        allObserver = null;
      }
    }

    function getOrCreateInfiniteEl(panel) {
      let el = panel.querySelector('[data-kbt-infinite]');
      if (el) return el;
      el = document.createElement('div');
      el.className = 'kbt-infinite';
      el.setAttribute('data-kbt-infinite', '');
      el.hidden = true;
      el.innerHTML = `
        <div class="kbt-infinite__sentinel" data-kbt-infinite-sentinel></div>
        <div class="kbt-infinite__loader" data-kbt-infinite-loader aria-hidden="true">
          <span class="kbt-infinite__spinner"></span>
        </div>
      `;
      panel.appendChild(el);
      return el;
    }

    function setInfiniteVisible(panel, visible) {
      let el = panel.querySelector('[data-kbt-infinite]');
      if (!visible) {
        if (el) el.hidden = true;
        return;
      }
      el = getOrCreateInfiniteEl(panel);
      el.hidden = false;
      const loader = el.querySelector('[data-kbt-infinite-loader]');
      if (loader) loader.hidden = true;
    }

    function setInfiniteLoading(panel, loading) {
      const el = panel.querySelector('[data-kbt-infinite]');
      if (!el) return;
      const loader = el.querySelector('[data-kbt-infinite-loader]');
      if (loader) loader.hidden = !loading;
    }

    function renderProducts(grid, products, { append = false } = {}) {
      const html = products.map((p) => buildCardHTML(p, config)).join('');
      if (append) {
        grid.insertAdjacentHTML('beforeend', html);
      } else {
        grid.innerHTML = html;
      }
      bindCardEvents(grid);
      const sectionRoot = grid.closest('[data-kbt-section]');
      if (sectionRoot && stickyItems.size > 0) {
        updateStickyBar(sectionRoot);
      }
    }

    function mergeProducts(lists) {
      const merged = [];
      const seen = new Set();
      lists.forEach((list) => {
        if (!list) return;
        list.forEach((product) => {
          const id = product?.id;
          if (!id || seen.has(id)) return;
          seen.add(id);
          merged.push(product);
        });
      });
      return merged;
    }

    async function ensureCollectionCached(collectionId) {
      const existing = cache.get(collectionId);
      if (existing?.complete) return existing.products;

      const products = await fetchAllCollectionProducts(collectionId);
      if (products !== null) cache.set(collectionId, { products, complete: true });
      return products;
    }

    function appendAllPage(panel, grid) {
      if (allLoadingMore || allRenderedCount >= allProductsList.length) return;

      allLoadingMore = true;
      setInfiniteLoading(panel, true);

      const next = allProductsList.slice(allRenderedCount, allRenderedCount + allPageSize);
      allRenderedCount += next.length;
      renderProducts(grid, next, { append: allRenderedCount > next.length });

      const hasMore = allRenderedCount < allProductsList.length;
      setInfiniteVisible(panel, hasMore);
      setInfiniteLoading(panel, false);
      allLoadingMore = false;

      if (!hasMore) disconnectAllObserver();
    }

    function setupAllInfiniteScroll(panel, grid) {
      disconnectAllObserver();
      const infiniteEl = getOrCreateInfiniteEl(panel);
      const sentinel = infiniteEl.querySelector('[data-kbt-infinite-sentinel]');
      if (!sentinel) return;

      const hasMore = allRenderedCount < allProductsList.length;
      setInfiniteVisible(panel, hasMore);
      if (!hasMore) return;

      allObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          if (panel.hidden || !panel.classList.contains('is-active')) return;
          appendAllPage(panel, grid);
        },
        { root: null, rootMargin: '400px 0px', threshold: 0 }
      );
      allObserver.observe(sentinel);
    }

    async function loadAllPanel(panel, grid) {
      const collectionIds = getTabCollectionIds();
      disconnectAllObserver();
      allProductsList = [];
      allRenderedCount = 0;
      allLoadingMore = false;
      setInfiniteVisible(panel, false);

      if (!collectionIds.length) {
        grid.innerHTML = `<p class="kbt-empty">${escapeHtml(
          config.emptyCollectionMessage || 'Select a collection for this tab.'
        )}</p>`;
        return;
      }

      grid.innerHTML = skeletonHTML(Math.min(skeletonCount * 2, 12));
      grid.setAttribute('aria-busy', 'true');

      const results = await Promise.all(
        collectionIds.map((id) => ensureCollectionCached(id))
      );
      const merged = mergeProducts(results);
      const allFailed = results.every((result) => result === null);

      if (merged.length) {
        allProductsList = merged;
        grid.innerHTML = '';
        appendAllPage(panel, grid);
        setupAllInfiniteScroll(panel, grid);
      } else if (allFailed) {
        grid.innerHTML = `<p class="kbt-empty">${escapeHtml(
          config.errorMessage ||
            'Unable to load products. Check Storefront API token in theme settings.'
        )}</p>`;
      } else {
        grid.innerHTML = `<p class="kbt-empty">${escapeHtml(
          config.noProductsMessage || 'No products found.'
        )}</p>`;
      }
      grid.removeAttribute('aria-busy');
    }

    async function loadPanel(panel) {
      const grid = panel.querySelector('[data-kbt-grid]');
      if (!grid) return;

      if (panel.hasAttribute('data-kbt-all')) {
        await loadAllPanel(panel, grid);
        return;
      }

      disconnectAllObserver();
      setInfiniteVisible(panel, false);

      const collectionId = panel.getAttribute('data-collection-id');

      if (!collectionId || collectionId === 'gid://shopify/Collection/') {
        grid.innerHTML = `<p class="kbt-empty">${escapeHtml(
          config.emptyCollectionMessage || 'Select a collection for this tab.'
        )}</p>`;
        return;
      }

      const cached = cache.get(collectionId);
      if (cached?.products) {
        renderProducts(grid, cached.products);
        return;
      }

      grid.innerHTML = skeletonHTML(skeletonCount);
      grid.setAttribute('aria-busy', 'true');

      const products = await ensureCollectionCached(collectionId);

      if (products && products.length) {
        renderProducts(grid, products);
      } else if (products && products.length === 0) {
        grid.innerHTML = `<p class="kbt-empty">${escapeHtml(
          config.noProductsMessage || 'No products found.'
        )}</p>`;
      } else {
        grid.innerHTML = `<p class="kbt-empty">${escapeHtml(
          config.errorMessage ||
            'Unable to load products. Check Storefront API token in theme settings.'
        )}</p>`;
      }
      grid.removeAttribute('aria-busy');
    }

    function activateTab(tab) {
      const tabId = tab.getAttribute('data-kbt-tab');
      tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        const match = panel.getAttribute('data-kbt-panel') === tabId;
        panel.classList.toggle('is-active', match);
        panel.hidden = !match;
        if (match) loadPanel(panel);
      });
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab));
    });

    if (prev && track) {
      prev.addEventListener('click', () => {
        track.scrollBy({ left: -220, behavior: 'smooth' });
      });
    }
    if (next && track) {
      next.addEventListener('click', () => {
        track.scrollBy({ left: 220, behavior: 'smooth' });
      });
    }
    if (track) {
      track.addEventListener('scroll', () => updateTabArrows(root), { passive: true });
      window.addEventListener('resize', () => updateTabArrows(root));
      updateTabArrows(root);
    }

    initStickyCartButton(root);
    // Restore sticky selections after refresh, then render bar
    loadStickyItems();
    updateStickyBar(root);
    // Swiper is deferred — refresh once so arrows show for slots > 6 on first paint.
    window.addEventListener('load', () => updateStickyBar(root), { once: true });
    window.addEventListener('resize', () => updateStickyBar(root));

    const firstActive =
      root.querySelector('[data-kbt-tab].is-active') || tabs[0];
    if (firstActive) activateTab(firstActive);
  }

  function boot() {
    document.querySelectorAll('[data-kbt-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-kbt-section]') || event.target;
    if (root?.matches?.('[data-kbt-section]')) initSection(root);
  });
})();
